import React, { useState, useCallback, useEffect } from 'react';
import styled from 'astroturf/react';
import { debounce } from 'underscore';

import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import CloseIcon from 'js/design_system/icons/close.svg';
import SearchIcon from 'js/design_system/icons/search.svg';
import commaizeInt from 'js/utils/commaize';

import { ResearchInterest } from 'js/videos/shared/research_interest_tags/types';
import {
  search as searchResearchInterests,
  searchSubtopics,
} from 'js/videos/shared/research_interest_tags/research_interest_search';

interface ResearchInterestDirectorySearchProps {
  fromTopicPage?: boolean;
  parentTopicId?: number;
}

export default function ResearchInterestDirectorySearch(
  props: ResearchInterestDirectorySearchProps,
) {
  const { fromTopicPage = false, parentTopicId } = props;

  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<Array<ResearchInterest>>(
    [],
  );

  // Hide the server rendered results if the user has provided a search query
  useEffect(() => {
    const serverRenderedResultsContainer = document.getElementById(
      'js-server-rendered-results',
    );

    if (!serverRenderedResultsContainer) {
      return;
    }

    if (query) {
      serverRenderedResultsContainer.classList.add('hidden');
    } else {
      serverRenderedResultsContainer.classList.remove('hidden');
    }
  }, [query]);

  // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(
      (query: string, onResults: (r: Array<ResearchInterest>) => void) => {
        if (parentTopicId) {
          searchSubtopics(query, parentTopicId).then(onResults);
        } else {
          searchResearchInterests(query, []).then(onResults);
        }
      },
      500,
    ),
    [parentTopicId],
  );

  const onSearchComplete = (results: Array<ResearchInterest>) => {
    setIsLoading(false);
    setSearchResults(results);
  };

  const onQueryInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    if (newQuery) {
      setIsLoading(true);
      debouncedSearch(newQuery, onSearchComplete);
    } else {
      setIsLoading(false);
      setSearchResults([]);
    }
  };

  const onClearSearch = () => {
    setQuery('');
    setIsLoading(false);
    setSearchResults([]);
  };

  const placeholderText = parentTopicId
    ? 'Search subtopics...'
    : fromTopicPage
      ? 'Search topics...'
      : '';

  return (
    <div>
      <InputContainer className={fromTopicPage ? 'full-width' : ''}>
        <SearchIcon svgProps={{ width: '16px', height: '16px' }} />

        <Input
          value={query}
          onChange={onQueryInput}
          placeholder={placeholderText}
        />

        <CloseIconWrapper onClick={onClearSearch}>
          <CloseIcon svgProps={{ width: '16px', height: '16px' }} />
        </CloseIconWrapper>
      </InputContainer>

      {isLoading && <LoadingIndicator className="fa fa-spinner fa-spin" />}

      {!isLoading && query && (
        <ResearchInterestSearchResults researchInterests={searchResults} />
      )}
    </div>
  );
}

function ResearchInterestSearchResults({
  researchInterests,
}: {
  researchInterests: Array<ResearchInterest>;
}) {
  if (researchInterests.length === 0) {
    return (
      <div className="topic-list-container">
        <Body size="md">No matching research topics found</Body>
      </div>
    );
  }

  return (
    <div className="topic-list-container">
      {researchInterests.map((ri) => (
        <div className="topic-container" key={ri.id}>
          <BodyLink href={ri.url} size="md" bold>
            {ri.displayName}
          </BodyLink>

          {Boolean(ri.weight && ri.weightDescription) && (
            <div className="topic-stats-container">
              <Body size="sm">
                {commaizeInt(ri.weight!)} {ri.weightDescription}
              </Body>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  max-width: 400px;
  padding: 0px 14px;
  border: 1px solid #dddde2;
  border-radius: 4px;
`;

const Input = styled.input`
  padding: 5px;
  border: none;
  margin-right: 5px;
  flex-grow: 2;
`;

const CloseIconWrapper = styled.span`
  cursor: pointer;
`;

const LoadingIndicator = styled.i`
  font-size: 42px;
  margin-top: 28px;
`;
